import type { DiagnosticSnapshot } from "@/api/diagnostic";

import { num, rec } from "../diagnosticData";

type Validation = DiagnosticSnapshot["validation"];

/** Uma linha da BOM vigente, como o domínio ``bom_lines`` do backend a manda. */
export interface CurrentBomLine {
  skuNumber: string;
  description: string;
  metric: string | undefined;
  /** ``pricing_app``, ``both`` (também na calculada) ou ``mygrow_only``. */
  origin: string;
  /** ``false`` na linha que só o myGROW tem: aparece e não soma. */
  inTotal: boolean;
  quantity: number | undefined;
  listPriceAnnual: number | undefined;
  /** O desconto que veio do Pricing App; o aplicado é ``effectiveDiscount`` (EF-FUNC-010). */
  discount: number | undefined;
  effectiveDiscount: number | undefined;
  /** Líquido da linha no período dela, dividido pelo período e vezes 12. */
  annualNet: number | undefined;
}

/** A BOM vigente gravada (hoje, a do Pricing App), lida ao vivo (#1345). */
export interface CurrentBom {
  version: number;
  source: string;
  /** TCV / período do contrato x 12, como o arquivo do Pricing App faz. */
  acv: number;
  tcv: number;
  totalList: number;
  discountPct: number;
  periodMonths: number | undefined;
  /** Linhas que entram no total. */
  skuLines: number;
  /** Quantidade das linhas com métrica "User" no total. */
  licensedUsers: number;
  /** Quando a versão foi gravada: comparada com a publicação da Calibragem. */
  createdAt: string | undefined;
  lines: CurrentBomLine[];
}

function text(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

/**
 * O projeto ainda não carregou o Pricing App: o domínio diz ``NO_BOM_VERSION``.
 *
 * Cartão 6.5 do Marco (22/10/2026): sem a BOM do Pricing App, o software do
 * Investimento não mostra valor, porque o da BOM calculada é preço de lista sem
 * desconto. Sem o envelope (api anterior ao domínio), não dá para afirmar nada.
 */
export function awaitingPricingAppBom(validation: Validation | undefined): boolean {
  return validation?.bom_lines?.code === "NO_BOM_VERSION";
}

/**
 * A BOM vigente do bloco ``validation``, ou ``undefined`` quando não há versão
 * gravada: aí o Ato 5 segue com a BOM calculada da seção ``bom_totals``.
 */
export function currentBom(validation: Validation | undefined): CurrentBom | undefined {
  const envelope = validation?.bom_lines;
  if (!envelope || envelope.state !== "ok") return undefined;
  const data = rec(envelope.data);
  const acv = num(data.acv);
  const tcv = num(data.tcv);
  if (acv === undefined || tcv === undefined) return undefined;

  const lines = (Array.isArray(data.lines) ? data.lines : []).map((raw): CurrentBomLine => {
    const item = rec(raw);
    return {
      skuNumber: text(item.sku_number) ?? "",
      description: text(item.description) ?? "",
      metric: text(item.sales_unit_metric),
      origin: text(item.origin) ?? "pricing_app",
      inTotal: item.in_total !== false,
      quantity: num(item.qty_current),
      listPriceAnnual: num(item.list_price_annual),
      discount: num(item.discount),
      effectiveDiscount: num(item.effective_discount),
      annualNet: num(item.annual_net),
    };
  });

  return {
    version: num(data.version) ?? 0,
    source: text(data.source) ?? "pricing_app_upload",
    acv,
    tcv,
    totalList: num(data.total_list) ?? 0,
    discountPct: num(data.discount_pct) ?? 0,
    periodMonths: num(data.contract_period_months),
    skuLines: num(data.sku_lines) ?? lines.filter((l) => l.inTotal).length,
    licensedUsers: num(data.licensed_users) ?? 0,
    createdAt: text(data.created_at),
    lines,
  };
}
